import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "/logo-transparent.png";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { AddCircle, Book, Buildings2, Calendar, CalendarAdd, ChatSquare, ClockCircle, DollarMinimalistic, Help, Notebook, Settings, UsersGroupRounded, VolumeLoud, Widget } from "@solar-icons/react"
import { useActiveBusinessId } from "@/hooks/use-business";
import { useBusinessOperationalAccess } from "@/hooks/use-business-operational-access";
import { isBusinessOperationalNavPath } from "@/lib/business-operational-access";
import { useGetSlotRequestPendingCountQuery } from "@/queries/slot-requests";
import { useGetConversationsQuery } from "@/queries/chat";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useTenantCapabilities } from "@/hooks/use-tenant-capabilities";

// Navigation items (stepId used for tour highlighting)
const navItems = [
  { titleKey: "nav.dashboard", url: "/dashboard", icon: Widget, stepId: "welcome dashboard" },
  { titleKey: "nav.appointments", url: "/appointments", icon: Calendar, stepId: "appointments" },
  { titleKey: "nav.slotRequests", url: "/slot-requests", icon: CalendarAdd, stepId: "slot-requests" },
  { titleKey: "nav.customers", url: "/customers", icon: Notebook, stepId: "customers" },
  { titleKey: "nav.messages", url: "/messages", icon: ChatSquare, stepId: "messages" },
  { titleKey: "nav.providers", url: "/providers", icon: UsersGroupRounded, stepId: "providers" },
  { titleKey: "nav.services", url: "/services", icon: ClockCircle, stepId: "services" },
  { titleKey: "nav.finance", url: "/finance", icon: DollarMinimalistic, stepId: "finance" },
  { titleKey: "nav.helpSupport", url: "/support", icon: Help, stepId: "support" },
  { titleKey: "nav.adCampaigns", url: "/ad-campaigns", icon: VolumeLoud, stepId: "ad-campaigns" },
  { titleKey: "nav.businesses", url: "/business", icon: Buildings2, stepId: "business" },
  { titleKey: "nav.settings", url: "/settings", to: "/settings/profile", icon: Settings, stepId: "settings" },
] as const;

interface Business {
  _id?: string;
  name?: string;
  image?: string;
  description?: string;
}

interface User {
  name?: string;
  email?: string;
}

interface AppSidebarProps {
  business?: Business;
  businesses?: Business[];
  user?: User;
  onStartTour?: () => void;
  onBusinessChange?: (business: Business) => void;
}

export function BusinessSidebar({ business, businesses = [], onStartTour }: AppSidebarProps) {
  const { t } = useTranslation("common");
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  const activeBusinessId = useActiveBusinessId();
  const { canUseOperationalFeatures, blockReason } = useBusinessOperationalAccess();
  const { canAccessNavPath } = useTenantCapabilities();
  const { data: pendingSlotRequests = 0 } = useGetSlotRequestPendingCountQuery(
    activeBusinessId ?? undefined,
  );
  const { data: conversationsData } = useGetConversationsQuery(
    activeBusinessId ?? undefined,
    { enabled: !!activeBusinessId },
  );
  const unreadMessages = conversationsData?.unreadCount ?? 0;

  const disabledTooltip =
    blockReason === "pending_approval"
      ? t("nav.disabledPendingApproval")
      : blockReason === "no_business"
        ? t("nav.disabledNoBusiness")
        : undefined;

  // Use businesses array if provided, otherwise create array from single business
  const businessesList = businesses.length > 0 ? businesses : (business ? [business] : []);
  const hasBusinesses = businessesList.length > 0;

  return (
    <Sidebar collapsible="icon">
      {/* Header with business switcher or placeholder */}
      <SidebarHeader className="py-3 overflow-hidden" data-sidebar="header">
        {hasBusinesses ? (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton size="lg" asChild>
                <Link to="/dashboard">
                  <img src={logo} alt="Woezor" className="h-14 w-auto object-cover" />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : (
          <SidebarMenu>
            <SidebarMenuItem>
              <div className="flex flex-col gap-3 p-3 rounded-lg border border-dashed border-muted-foreground/25 group-data-[collapsible=icon]:hidden">
                <div className="flex items-center gap-2">
                  <Buildings2 className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium text-muted-foreground">No Business or Institution</span>
                </div>
                <Button
                  onClick={() => navigate("/business/create")}
                  size="sm"
                  className="w-full gap-2"
                  variant="outline"
                >
                  <AddCircle className="h-4 w-4" />
                  Create Business or Institution
                </Button>
              </div>
              {/* Icon-only version when collapsed */}
              <SidebarMenuButton
                onClick={() => navigate("/business/create")}
                tooltip="Create Business or Institution"
                className="group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-2 hidden"
              >
                <AddCircle className="h-4 w-4" />
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarHeader>

      {/* Main content */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {navItems
                .filter((item) => canAccessNavPath(item.url))
                .map((item) => {
                const isActive =
                  item.url === "/dashboard"
                    ? currentPath === "/" || currentPath === "/dashboard"
                    : currentPath.startsWith(item.url);

                const isBusinessDependent = isBusinessOperationalNavPath(item.url);
                const isDisabled = isBusinessDependent && !canUseOperationalFeatures;

                return (
                  <SidebarMenuItem key={item.titleKey}>
                    <SidebarMenuButton
                      asChild={!isDisabled}
                      isActive={isActive}
                      tooltip={isDisabled ? disabledTooltip : t(item.titleKey)}
                      disabled={isDisabled}
                      data-tour-step-id={item.stepId}
                      onClick={isDisabled ? (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      } : undefined}
                    >
                      {isDisabled ? (
                        <>
                          <item.icon />
                          <span>{t(item.titleKey)}</span>
                        </>
                      ) : (
                        <Link to={"to" in item && item.to ? item.to : item.url}>
                          <item.icon />
                          <span>{t(item.titleKey)}</span>
                          {item.url === "/slot-requests" && pendingSlotRequests > 0 ? (
                            <SidebarMenuBadge className="bg-destructive text-destructive-foreground">
                              {pendingSlotRequests > 9 ? "9+" : pendingSlotRequests}
                            </SidebarMenuBadge>
                          ) : null}
                          {item.url === "/messages" && unreadMessages > 0 ? (
                            <SidebarMenuBadge className="bg-destructive text-destructive-foreground">
                              {unreadMessages > 9 ? "9+" : unreadMessages}
                            </SidebarMenuBadge>
                          ) : null}
                        </Link>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      {/* Footer with System Tour */}
      <SidebarFooter>
        {onStartTour ? (
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={onStartTour} tooltip="Tutorial Guide">
                <Book className="text-primary" />
                <span>{t("nav.tutorialGuide")}</span>
                <span className="relative ml-auto flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : null}
      </SidebarFooter>

      {/* Rail for resize/collapse */}
      <SidebarRail />
    </Sidebar>
  );
}
